import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  ErrorFeedback,
  Fieldset,
  HintToggleButton,
  If,
  InputText,
  Legend,
  Loader,
} from '@paytrace/pt-skel/Component'
import { AppCtx } from '@paytrace/pt-skel/Context'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import BaseEvent from '@paytrace/pt-skel/Event'
import { getLegacyDomain, getSubmittedData } from '@paytrace/pt-skel/Helper'
import {
  BatchTransactionErrorFeedback,
  BatchTransactionFeedback,
} from '../Component'
import * as util from '../Helper/util'
import Event from '../Event'
import { formKeysToObjects } from '../Helper/Form'
import { hasPermission, LegacyPermission } from '../Helper/HasPermission'
import * as T from '../types'
import { ProcessButton } from '../Component/ProcessButton'

interface Customer {
  card_masked: string
  cid: string
  customer_id: string
  customer_label: string
  exp_month: string
  exp_year: string
  email?: string
}

interface Props {}

const FIRST_DEFAULT = 1
const LAST_DEFAULT = 10

const dialogStyle = {
  background: '#fff',
  left: '20%',
  width: '60%',
  padding: '15px',
  border: '4px solid red',
  margin: 'auto',
  borderRadius: '10px',
  textAlign: 'center' as any, // No clue why tsc is complaining here...
  position: 'fixed' as any,
  zIndex: '99',
}

const dialogHeaderStyle = {
  textAlign: 'center' as any,
  fontSize: '25px',
  fontWeight: 'bold',
  color: '#5aa2cd',
}

function getFakeTransactionId () {
  let i = 0

  return () => 'fake' + i++
}

function formDataToReq (formData: { [key: string]: { [key: string]: string }}) {
  return Object.keys(formData).map(customer_id => {
    const item: { [key: string]: number | string | boolean } = {
      customer_id: Number(customer_id),
      transaction_type: formData[customer_id].tranx.toLowerCase(),
      amount: Number(formData[customer_id].amount),
      invoice_number: formData[customer_id].inv,
      csc: formData[customer_id].csc,
      email_receipt: formData[customer_id].RECEIPT === 'Y',
      // Not used by backend, just for feedback render
      customer_label: formData[customer_id].customer_label,
    }

    if (!item.csc) {
      delete item.csc
    }

    return item
  }).filter(x => Number(x.amount) > 0)
}

function resetRows () {
  (document.querySelector('#multi-tranx-form') as any).reset()
}

const self1 = Symbol('MultipleTransactions1')
const self2 = Symbol('MultipleTransactions2')
const self3 = Symbol('MultipleTransactions3')

export function MultipleTransactions (props: Props) {
  const context = useContext(AppCtx)
  const [surchargeAck, setSurchargeAck] = useState(false)
  const [isSurchargeEnabled, setIsSurchargeEnabled] = useState(false)
  const [showSurchargeAgreement, setShowSurchargeAgreement] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [reqBatchItems, setReqBatchItems] = useState<{ [key: string]: string | number | boolean }[]>([])

  const getAssociatedCustomerRequestEvent = useCallback(() => {
    return context.records.associatedMerchantList && context.records.misc.is_merchant_user
      ? Event.GET_CUSTOMERS_FROM_TOKEN_REQUEST
      : Event.GET_CUSTOMER_LIST_REQUEST
  }, [context.records.associatedMerchantList, context.records.misc.is_merchant_user])

  useEffect(() => {
    PubSub.sub(self1, Event.GET_CUSTOMER_LIST_RESPONSE, (customers: Customer[]) => {
      const customerList: Customer[] = customers.map((customer: Customer) => {
        return {
          ...customer,
          cid: customer.customer_id,
        }
      })
      setCustomers(customerList)
    })
    const associatedCustomerRequestEvent = getAssociatedCustomerRequestEvent()

    const customerRequestData = {
      name_filter: '',
      offset: FIRST_DEFAULT - 1,
      limit: LAST_DEFAULT - (FIRST_DEFAULT - 1),
      include_card_number: true,
      last_activity_days: null,
      include_associated_merchants: true,
      ...(associatedCustomerRequestEvent === Event.GET_CUSTOMER_LIST_REQUEST && { merchantId: context.merchant }),
    }

    PubSub.pub(associatedCustomerRequestEvent, customerRequestData)

    PubSub.pub(Event.GET_MERCHANT_SURCHARGE_INFO_REQUEST, context.merchant)

    PubSub.sub(self1, Event.GET_MERCHANT_SURCHARGE_INFO_RESPONSE, (response: T.StatusAndResponse<T.MerchantSurchargeInfo>) => {
      if (response.status === 'error') {
        return
      }

      const surchargeInfo = response.data
      const showPopup =
        surchargeInfo.can_merchant_surcharge ||
        (surchargeInfo.is_surcharge_monthly_product && surchargeInfo.is_surcharge_enabled)

      if (showPopup) {
        setIsSurchargeEnabled(true)
      }
    })

    return () => {
      PubSub.unsub(self1)
    }
  }, [context.merchant, context.records.associatedMerchantList, context.records.misc.is_merchant_user, getAssociatedCustomerRequestEvent])

  useEffect(() => {
    PubSub.sub(self2, Event.RUN_MULTIPLE_TRANX_RESPONSE, (rawJson) => {
      window.scrollTo(0, 0)
      setIsLoading(false)

      // Some unexpected error was hit, ugh - this should never be the case, so
      // dump as much info as we can.
      if ('err' in rawJson) {
        const msg = 'An unexpected error was encountered, full info: ' + JSON.stringify(rawJson.err)
        PubSub.pub(Event.NETWORK_ERROR, msg)
        // setHideRows(false)

        return
      }

      // setHideRows(true)
      // If we got here, we got the expected type of response
      const json: any[] = rawJson
      const good: any[] = []
      const bad: any[] = []

      // For the response_codes:
      // http://localhost:8100/redoc#tag/Response-Codes
      // 101 = successful sale
      const successCodes = [
        101, // sale
        106, // refunded
      ]
      const getTransactionIdFn = getFakeTransactionId()

      json.forEach((response: any) => {
        let responseCode = 0

        // We have to have a transaction_id for feedback, but if these types
        // of requests fail, we won't have one in the return data, so we
        // make a fake one to align amounts on the lookup.
        if (Array.isArray(response.data)) {
          responseCode = response.data[0].response_code
          response.transaction_id = response.data[0].transaction_id || getTransactionIdFn()
        } else {
          responseCode = response.data.response_code
          response.transaction_id = response.data.transaction_id || getTransactionIdFn()
        }

        if (response.status_code === 200 &&
            successCodes.includes(responseCode)) {
          good.push(response)
        } else {
          bad.push(response)
        }
      })

      const records: { [key: string]: any } = {}

      reqBatchItems.forEach((item: any, i: number) => {
        const idx = json[i].transaction_id
        records[idx] = {
          ...item,
          requested_amount: item.amount,
          preamble: `Transaction for customer "${item.customer_label}"`,
        }
      })

      PubSub.pub(Event.BATCH_TRANSACTION_ERROR_FEEDBACK, { response: bad, records })
      PubSub.pub(Event.BATCH_TRANSACTION_FEEDBACK, { response: good, records })
    })

    return () => {
      PubSub.unsub(self2)
    }
  }, [context.merchant, reqBatchItems])

  useEffect(() => {
    PubSub.sub(self3, Event.FILTER_CUSTOMER_LIST, (json: any) => {
      const first = Number(json['First Customer'])
      const last = Number(json['Last Customer'])
      const last_activity_days = Number(json['Window of Activity'])

      if (Number.isNaN(last_activity_days)) {
        return alert('Window of Activity must be numeric.')
      }

      if (Number.isNaN(first)) {
        return alert('First Customer must be numeric.')
      }

      if (first < 1) {
        return alert('First Customer must be greater than 0.')
      }

      if (Number.isNaN(last)) {
        return alert('Last Customer must be numeric.')
      }

      if (last < first) {
        return alert('Last Customer must be greater than First Customer.')
      }

      const associatedCustomerRequestEvent = getAssociatedCustomerRequestEvent()

      const customerRequestData = {
        name_filter: '',
        offset: first - 1,
        limit: last - (first - 1),
        include_card_number: true,
        last_activity_days: null,
        include_associated_merchants: true,
        ...(associatedCustomerRequestEvent === Event.GET_CUSTOMER_LIST_REQUEST && { merchantId: context.merchant }),
      }

      PubSub.pub(associatedCustomerRequestEvent, customerRequestData)
    })

    return () => {
      PubSub.unsub(self3)
    }
  }, [context.merchant, context.records.associatedMerchantList, context.records.misc.is_merchant_user, getAssociatedCustomerRequestEvent])

  const filter = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    PubSub.pub('VALIDATION_ERROR::clear')

    // TODO: properly map to the customer filter request
    const formData = getSubmittedData(e)
    PubSub.pub(
      Event.FILTER_CUSTOMER_LIST,
      { ...formData }
    )

    return false
  }

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    PubSub.pub('VALIDATION_ERROR::clear')

    const formData = formKeysToObjects(getSubmittedData(e) as { [key: string]: string })
    const batchItems = formDataToReq(formData)

    if (batchItems.length === 0) {
      return alert('Please specify at least one transaction amount.')
    }

    if (!confirm(`Are you sure you want to process ${batchItems.length} transactions?`)) {
      return
    }

    setReqBatchItems(batchItems)

    if (isSurchargeEnabled) {
      setShowSurchargeAgreement(true)
      return false
    }

    // If surcharge IS enabled, this code exists is handled in processSurcharge
    setIsLoading(true)
    setSurchargeAck(false)

    PubSub.pub(Event.BATCH_TRANSACTION_ERROR_FEEDBACK, { records: {}, response: [] })
    PubSub.pub(Event.BATCH_TRANSACTION_FEEDBACK, { records: {}, response: [] })
    PubSub.pub(BaseEvent.NETWORK_ERROR_CLEAR)
    PubSub.pub(
      Event.RUN_MULTIPLE_TRANX_REQUEST,
      {
        merchant_id: context.merchant,
        batch_items: batchItems,
      }
    )

    return false
  }

  function processSurcharge () {
    setShowSurchargeAgreement(false)
    setIsLoading(true)
    setSurchargeAck(false)

    PubSub.pub(Event.BATCH_TRANSACTION_ERROR_FEEDBACK, { records: {}, response: [] })
    PubSub.pub(Event.BATCH_TRANSACTION_FEEDBACK, { records: {}, response: [] })
    PubSub.pub(BaseEvent.NETWORK_ERROR_CLEAR)
    PubSub.pub(
      Event.RUN_MULTIPLE_TRANX_REQUEST,
      {
        merchant_id: context.merchant,
        batch_items: reqBatchItems,
      }
    )
  }
  return (
    <>
      <div className="float-right">
        <HintToggleButton />
        <a className='help-link' href="https://paytrace.atlassian.net/wiki/spaces/HD/pages/2027258432/Multiple+Transactions">Help</a>
      </div>
      <h1>Multiple Transactions</h1>
      <hr className="clear"/>

      <If is={showSurchargeAgreement === true}>
        <div id="dialog-confirm"
             style={dialogStyle}>
          <p style={dialogHeaderStyle}>Action Required!</p>
          <p style={{ textAlign: 'center', fontSize: '15px' }}>
            You are processing transactions that will incur a surcharge fee.
            You are required to offer the cardholder(s) additional payment
            methods to avoid the surcharge fee.
          </p>
          <p style={{ textAlign: 'center', fontSize: '13px' }}>
            <input id="uid-surcharge_ack"
                   checked={surchargeAck}
                   onClick={() => setSurchargeAck(!surchargeAck)}
                   type="checkbox" />
              I have offered the cardholder(s) alternative payment methods and
              they have chosen to proceed with the current payment method and incur
              the surcharge fee.
          </p>
          <p style={{ textAlign: 'center' }}>
            <button
              onClick={() => setShowSurchargeAgreement(false)}
              className="float-right">Cancel</button>
            <button
              onClick={() => surchargeAck
                ? processSurcharge()
                : alert('Please acknowledge by clicking the checkbox, or cancel.')}
              className="primary-btn form-btn float-right">Process</button>
          </p>
        </div>
      </If>
      <BatchTransactionFeedback action='transaction' merchantId={Number(context.merchant)} include_surcharge={isSurchargeEnabled}/>
      <BatchTransactionErrorFeedback action='transaction' />

      <FilterForm onSubmit={filter}/>

      <TransactionForm isLoading={isLoading} onSubmit={submit} customers={customers} records={context.records}/>
    </>
  )
}

function FilterForm (props: { onSubmit: (e: React.FormEvent<HTMLFormElement>) => any }) {
  return (
    <form onSubmit={props.onSubmit}>
      <Fieldset>
        <Legend title="Filter Customers"/>

        <InputText
          label="Window of Activity"
          hintText='This value will be used to filter customers so that only those who have processed transactions in this number of days will be displayed below.'
        />

        <InputText
          label="First Customer"
          defaultValue={FIRST_DEFAULT}
          hintText='Specify which logical customer, sorted by the customer ID alphabetically, should be the first to appear in the list below.'
        />

        <InputText
          label="Last Customer"
          defaultValue={LAST_DEFAULT}
          hintText='Specify which logical customer, sorted by the customer ID alphabetically, should be the last to appear in the list below.'
        />

        <div className="spacer">
          <ErrorFeedback name="root"/>
          <button onClick={resetRows} className="form-btn other-btn">&bull; Filter</button>
        </div>
      </Fieldset>
    </form>
  )
}

interface TransactionProps {
  customers: Customer[]
  isLoading: boolean
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => any
  records: { merchant: T.MerchantListRecord },
}

interface TransactionFormRowProps {
  customer: Customer
  n: number
  merchant: T.MerchantListRecord
}

const legacyDomain = getLegacyDomain()

function getCustLink (cid: string) {
  return `${legacyDomain}/mcust.pay?cid=${cid}`
}

function getAllowedTransactionOption (records: T.PermissionCheckProps['records']) {
  const options = [
    { value: 'sale', label: 'Sale', permission: LegacyPermission.SALE },
    { value: 'auth-only', label: 'Auth Only', permission: LegacyPermission.ONLY },
    { value: 'refund', label: 'Refund', permission: LegacyPermission.REFUND },
    // { value: 'Str/Fwd', label: 'Str/Fwd', permission: '' },
    // { value: 'CC', label: 'Csh/Chk', permission: '' },
  ]
  const filteredOptions = options.filter(option => hasPermission({ records, required_permissions: [option.permission] }))
  return filteredOptions.map(option => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ))
}

function TransactionFormRow (props: TransactionFormRowProps) {
  // const rowSelf = Symbol(`TransactionFormRow${props.n}`)
  const context = useContext(AppCtx)
  const INVALID_CARD = '(invalid card)'
  const DEFAULT_CARD = '(invalid card)' // '...'
  const { card_masked, cid, customer_label, exp_month, exp_year, email } = props.customer
  const { require_invoice_number = false, require_csc = false } = props.merchant
  const cardMasked = card_masked || DEFAULT_CARD
  const [emailReceipt, setEmailReceipt] = useState(false)

  // Keeping commented for future reference, but not necessary now as all the
  // details come through in the list data (if we ever want to lazy load it,
  // we can re-implement here).
  /* useEffect(() => {
   *   // For each customer record in list, get the full details
   *   if (!card_masked) {
   *     PubSub.pub(Event.GET_CUSTOMER_REQUEST, { customerId: cid })
   *   }

   *   PubSub.sub(self, Event.GET_CUSTOMER_RESPONSE, ({ customer }: { customer: Customer }) => {
   *     // Ignore receiving other row events
   *     if (Number(customer.customer_id) !== Number(cid)) {
   *       return
   *     }

   *     setCardMasked(customer.card_masked || INVALID_CARD)
   *   })

   *   return () => {
   *     PubSub.unsub(rowSelf)
   *   }
   * }, [card_masked, cid, rowSelf]) */

  const invoiceRef = useRef<HTMLInputElement>(null)
  const cscRef = useRef<HTMLInputElement>(null)

  const isCardExpired: boolean = util.isCardExpired(exp_month, exp_year)
  const isCardInvalid: boolean = [INVALID_CARD, DEFAULT_CARD].includes(cardMasked)
  const disabledRow = isCardInvalid || isCardExpired
  const styles = disabledRow ? { background: '#ffcccc' } : {}
  const warning = 'The card on file is expired or corrupted and is not able to process.'

  // We only show the email when it's on file.
  const emailBoxStyle = email ? {} : { display: 'none' }

  const handleFieldRequirement = (
    ref: React.RefObject<HTMLInputElement>,
    isRequired: boolean,
    placeholderRequired: string,
    placeholderOptional: string
  ) => {
    if (ref.current) {
      ref.current.required = isRequired
      ref.current.placeholder = isRequired ? placeholderRequired : placeholderOptional
    }
  }

  return (
    <tr
      key={`mtc-${props.n}`}
      className="inline-form"
      style={styles}
      title={disabledRow ? warning : ''}>
      <td><input
            name={`customer[${cid}][amount]`}
            maxLength={17}
            defaultValue={0}
            disabled={disabledRow}
            onChange={(e) => {
              const value = parseFloat(e.target.value)
              const isAmountGreaterThanZero = value > 0

              handleFieldRequirement(invoiceRef, require_invoice_number && isAmountGreaterThanZero, 'Required', 'Optional')
              handleFieldRequirement(cscRef, require_csc && isAmountGreaterThanZero, 'Required', 'Optional')
            }
          }
          /></td>
      <td><Link to={getCustLink(cid)} target="_blank">{customer_label}</Link></td>
      <td>{(isCardExpired && !isCardInvalid) ? `(expired card - ${exp_month}/${exp_year})` : cardMasked}</td>
      <td>
        <select name={`customer[${cid}][tranx]`}>
          {getAllowedTransactionOption(context.records)}
        </select>
      </td>

      <td><input name={`customer[${cid}][inv]`} maxLength={50} ref={invoiceRef} placeholder={require_invoice_number ? 'Required' : 'Optional' }/></td>
      <td><input name={`customer[${cid}][csc]`} maxLength={4} ref={cscRef} placeholder={require_csc ? 'Required' : 'Optional' }/></td>
      <td><input style={emailBoxStyle} type="checkbox" onClick={() => setEmailReceipt(!emailReceipt)} /></td>
      <input type="hidden" name={`customer[${cid}][RECEIPT]`} value={emailReceipt ? 'Y' : 'N'}/>
      <input type="hidden" name={`customer[${cid}][customer_label]`} value={customer_label} />
    </tr>
  )
}

function TransactionForm (props: TransactionProps) {
  const context = useContext(AppCtx)
  const { customers, onSubmit, records: { merchant } } = props
  const rows = customers.map((customer, n) => {
    return <TransactionFormRow key={n} n={n} customer={customer} merchant={merchant} />
  })

  const loaderStyle = props.isLoading ? { display: 'block' } : { display: 'none' }
  const mainStyle = props.isLoading ? { display: 'none' } : { display: 'block' }

  return (
    <>
      <Loader style={loaderStyle}/>
      <form id="multi-tranx-form" onSubmit={onSubmit} style={mainStyle}>
        <Fieldset>
          <Legend title="Customers"></Legend>
          <table>
            <thead>
            <tr>
              <th>Amount</th>
              <th>Customer</th>
              <th>Card</th>
              <th>Type</th>
              <th>Invoice</th>
              <th>CSC</th>
              <th>Email</th>
            </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </Fieldset>

        <div className="warning"><strong>{customers.length}</strong> customer(s) are currently displayed in the table
          above.
        </div>
        <ProcessButton records={context.records} required_permissions={[LegacyPermission.SALE, LegacyPermission.IS_PROFESSIONAL]} />
      </form>

      <div className='warning spacer'> <strong>Warning</strong> - To prevent duplicates, do not click this button more than once or refresh the browser. </div>
    </>
  )
}

export default MultipleTransactions
